import { Box, FormControl, Grid, InputAdornment, List, ListItem, ListItemText, useTheme } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useForm, useWatch } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import CustomDatePicker from "../../common/CustomDatePicker";
import dayjs from "dayjs";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  SingleAlertInfo,
  fetchPracticeLookupByUserRole,
  fetchMac,
  fetchManufacturersList,
  setSingleAlertObj,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import CustomTable from "../../common/CustomTable";
import {
  PracticeInfo,
} from "../../../redux/slice/practiceSlice";
import {
  AddCircleOutlineOutlined,
  BoltOutlined,
  Cancel,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  Details,
  Inventory2Outlined,
  Save,
} from "@mui/icons-material";
import {
  calculateValue,
  parseAmount,
  priceFormatter,
  sortNames,
} from "../../../utils/Utility";
import { TIER_PRICE_CODETABLE } from "../../../constants/applicationConstants";
import { RootState } from "../../../redux/store";
import { selectIsTokenAvailable } from "../../../redux/slice/authSlice";
import { getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import {
  createProductAgreement,
  PracticeProductInfo,
} from "../../../redux/slice/productAgreement/createAgreementSlice";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import PracticeDetailsPopup from "./PracticeDetailsPopup";
import {
  ProductInfo,
  ProductPrice,
  ProductPriceByProductRequest,
  fetchProductsByUserRoleData,
  getProductPriceByProductId,
  updateElementInProductListSlice,
  useProductListReducer,
} from "../../../redux/slice/product/productListSlice";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import {
  fetchAllUserRoles,
} from "../../../redux/slice/userSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomTextField from "../../common/CustomTextField";
import CustomRadioButton from "../../common/CustomRadioButton";
import CustomCheckbox from "../../common/CustomCheckBox";
import { MSG_ACTIVE_PRODUCT_AGREEMENT_FOUND, RESPONSE_CODES } from "../../../constants/applicationConstants";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { fetchPracticeProductsData, usePracticeProductsSlice } from "../../../redux/slice/practice/practiceProductSlice";
import { fetchPracticeByIdData, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Typography,
  Paper,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { tokens } from "../../../theme";
import CustomIconButton from "../../common/CustomIconButton";
import { useNavigation } from "../../../hooks/useNavigation";
import { getProductConfigByProduct, productPriceByProductId } from "../../../service/ProductService";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

// Sample data
const mainRow = {
  name: 'Agreement Name',
  description: 'Main agreement details.',
  subTable: [
    { key: 'Sub Item 1', value: 'Detail 1' },
    { key: 'Sub Item 2', value: 'Detail 2' },
    { key: 'Sub Item 3', value: 'Detail 3' },
  ],
};

interface CreateNewAgreementNewProps {}

interface ProductRow {
  productId : number;
  productPriceId: number;
  name: string;
  sku?: string;
  price?: string;
  discount?: string;
  extendedPrice?: string;
  allowableUnitPrice?: number; // CS-1845
  upfrontPricingEnabled?: boolean; // CS-1845
  upfrontDiscount?: number; // CS-1845
  defaultUpfrontDiscount?: number; // CS-2070 - product-config default, kept to detect overrides
  defaultLateFeePercent?: number; // CS-2070 - product-config default, kept to detect overrides
  lateFeeEnabled?: boolean; // CS-1764
  lateFeePercent?: number; // CS-1764
  extra?: boolean;
  customPrice?: number | null; //CS-1337
  tier?: React.ReactNode;
}

// CS-1845 START - Upfront pricing product config support
interface AgreementProductConfigRule {
  productConfigId?: number;
  productId?: number;
  configType: string;
  configCode: string;
  value: string;
  metadata?: Record<string, unknown>;
}

type UpfrontPricingConfig = {
  enabled: boolean;
  upfrontDiscount: number;
};

// CS-1764 - Late fee config type
type LateFeeConfig = {
  enabled: boolean;
  lateFeePercent: number;
};

const PRODUCT_CONFIG_TYPE_UPFRONT_PRICING = "UPFRONT_PRICING";
const PRODUCT_CONFIG_TYPE_PURCHASE_AND_BILL = "PURCHASE_AND_BILL";
const PRODUCT_CONFIG_TYPE_AGREEMENT = "AGREEMENT";
const PRODUCT_CONFIG_CODE_MODE = "MODE";
// const AGREEMENT_MODE_MANDATORY = "MANDATORY"; // used by isGroupingEligibleProduct (commented out)
// CS-1764 - Late fee constants
const PRODUCT_CONFIG_TYPE_LATE_FEE = "LATE_FEE";
const LATE_FEE_PERCENT_METADATA_KEY = "lateFeePercent";
const PRODUCT_CONFIG_CODE_ENABLE = "ENABLE";
const PRODUCT_CONFIG_VALUE_YES = "Y";
const UPFRONT_DISCOUNT_METADATA_KEY = "upfrontDiscount";
// CS-1845 END


// Define the grouped row type
interface GroupedRow {
  mainRow: ProductRow;
  subRows: ProductRow[];
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "left" | "center" | "right";
  format?: (value: number) => string;
}

interface Product {
  productId?: string;
  name?: string;
  discount: JSX.Element;
  price: string | null;
  extendedPrice: any;
  productPriceId: string | null;
  close: JSX.Element;
}

type NewAgreementState = {
  practiceId: string | null;
  product: string | null;
  practice: PracticeInfo[];
  practiceName: string | null;
  effectiveDate: string | null;
  // inactiveDate: string | null;
  macId: string | null;
  manufacturerId: string | number | null;
  practiceProducts: PracticeProductInfo[];
  extendedValues: string[] | null;
  discount: string[] | null;
  radio: string[] | null;
  customPrice: string[] | null;
  agreementMode: string;
  groupingChecked: boolean;
  groupingProductIds: (string | number)[];
};

type CreateAgreementRouteState = {
  openFromAgreementRequestList?: boolean;
  agreementRequestId?: number;
  practiceId?: number;
  productId?: number;
};

const ALL_GROUPING_PRODUCTS_VALUE = "ALL_PRODUCTS";

const isGroupingEnabledManufacturer = (manufacturer?: {
  agreementGrouping?: string;
}) => manufacturer?.agreementGrouping === "Y";

const initialAgreement: NewAgreementState = {
  practiceId: null,
  product: null,
  practiceName: null,
  effectiveDate: null,
  practice: [],
  // inactiveDate: null,
  macId: null,
  manufacturerId: null,
  practiceProducts: [],
  extendedValues: [],
  discount: [],
  radio: [],
  customPrice: [],
  agreementMode: "SINGLE",
  groupingChecked: false,
  groupingProductIds: [],
};

const productColumns: Column[] = [
  { id: "name", label: "Product", minWidth: 100 },
  { id: "price", label: "Allowable", minWidth: 100},
  { id: "discount", label: "Discount", minWidth: 100, align: "center" },
  { id: "tier", label: "Tier", minWidth: 150 },
  { id: "extendedPrice", label: "Extended Price", minWidth: 100},
  { id: "action", label: "Action", minWidth: 50 },
];


const subColumns: Column[] = [
  { id: "sku", label: "SKU", minWidth: 100 },
  { id: "priceAllowable", label: "Allowable", minWidth: 100},
  { id: "discount", label: "Discount", minWidth: 100, align: "center" },
  { id: "tier", label: "Tier", minWidth: 150 },
  { id: "extendedPrice", label: "Extended Price", minWidth: 100},
];


type Option = {
  value: string | number;
  label: string;
};

const CreateNewAgreementNew: React.FC<CreateNewAgreementNewProps> = () => {
  const { control, handleSubmit, reset, watch, getValues, setValue, formState: { errors } } =
    useForm<NewAgreementState>({ defaultValues: initialAgreement });
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const location = useLocation();
  const routeState = (location.state as CreateAgreementRouteState) || {};
  const openedFromAgreementRequestList = Boolean(routeState.openFromAgreementRequestList);
  const requestedPracticeId = Number(routeState.practiceId);
  const requestedProductId = Number(routeState.productId);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    primaryBtnSx,
    cancelBtnSx,
    inputFieldSx,
    dropdownWrapSx,
    datepickerWrapSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );
  const outlineActionBtnSx = {
    ...cancelBtnSx,
    color: mTokens.accent,
    borderColor: `${mTokens.accent}8c`,
    backgroundColor: isDarkModal ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    height: "42px",
    "&:hover": {
      color: "#fff",
      borderColor: mTokens.accent,
      backgroundColor: mTokens.accent,
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": { color: "#fff" },
    },
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };
  const productTableWrapSx = {
    border: `1px solid ${mTokens.border}`,
    borderRadius: "12px",
    overflow: "hidden" as const,
    backgroundColor: isDarkModal ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.85)",
  };
  const productTableSx = {
    "& .MuiTableCell-head": {
      fontWeight: 700,
      fontSize: "0.78rem",
      color: mTokens.textSecondary,
      backgroundColor: isDarkModal ? "rgba(15,23,42,0.55)" : "rgba(241,245,249,0.9)",
      borderBottom: `1px solid ${mTokens.border}`,
      whiteSpace: "nowrap" as const,
    },
    "& .MuiTableCell-body": {
      fontSize: "0.85rem",
      color: mTokens.textPrimary,
      borderBottom: `1px solid ${mTokens.border}`,
    },
  };
  // Compact modal-style inputs for Selected Products cells (match platform field chrome).
  const clickToSelectFieldWrapSx = (width = 140) => ({
    ...tableFieldWrapSx(width),
    cursor: "pointer",
    "& .Mui-disabled": { pointerEvents: "none" },
  });
  const focusFieldAfterSelect = (event: React.MouseEvent<HTMLElement>) => {
    const fieldWrap = event.currentTarget;
    setTimeout(() => fieldWrap.querySelector<HTMLInputElement>("input:not(:disabled)")?.focus(), 0);
  };
  const tableFieldWrapSx = (width = 140) => ({
    ...inputFieldSx(),
    width,
    minWidth: width,
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${mTokens.inputBg} !important`,
      borderRadius: "10px !important",
      fontSize: "0.82rem",
      color: mTokens.textPrimary,
      minHeight: "36px !important",
      height: "36px !important",
      boxShadow: isDarkModal ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: `${mTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: `${mTokens.accent} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${mTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiInputLabel-root, & label": { display: "none" },
    "& .MuiInputBase-input": {
      color: mTokens.textPrimary,
      fontSize: "0.82rem",
      py: "8px !important",
    },
    "& .MuiInputAdornment-root, & .MuiTypography-root": {
      color: mTokens.textSecondary,
      fontSize: "0.78rem",
    },
    "& .MuiFormHelperText-root": {
      mx: 0,
      mt: 0.5,
      fontSize: "0.68rem",
      lineHeight: 1.35,
      whiteSpace: "normal" as const,
    },
  });
  const tableRadioSx = {
    color: mTokens.textSecondary,
    p: 0.45,
    "&.Mui-checked": { color: `${mTokens.accent} !important` },
  };
  const tableRadioLabelSx = {
    m: 0,
    mr: 0.5,
    alignItems: "center",
    "& .MuiFormControlLabel-label": { mt: 0 },
  };
  const tableDeleteBtnSx = {
    borderRadius: "10px",
    border: `1px solid ${isDarkModal ? "rgba(248,113,113,0.45)" : "rgba(239,68,68,0.4)"}`,
    color: isDarkModal ? "#f87171" : "#dc2626",
    width: 34,
    height: 34,
    "&:hover": {
      borderColor: isDarkModal ? "#f87171" : "#dc2626",
      backgroundColor: isDarkModal ? "rgba(248,113,113,0.12)" : "rgba(239,68,68,0.08)",
    },
  };
  const { macInfoList, manufacturerInfoList } = useCommonReducer();
  const { productsByUserRole, ProductPriceListForSingleProduct } = useProductListReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const canManageTierPrice = useSelector((state: RootState) => selectIsTokenAvailable("canManageTierPrice")(state));
  const tierPriceBeforeSelectRef = React.useRef<Record<string, { extendedPrice: number | null; extendedPriceDisplay: string | null; appliedAmount: number; discountType?: string | number; radio: string }>>({});
  const tierCodeRows = useMemo(() => {
    const values = getCodeTableValuesByTableName(allCodeTablesValuesMap as any, TIER_PRICE_CODETABLE);
    return Array.isArray(values) ? values : [];
  }, [allCodeTablesValuesMap]);
  const [productRows, setProductRows] = React.useState<any[]>([]);
  const [practiceLookupOptions, setPracticeLookupOptions] = React.useState<Option[]>([]);
  const [showPracticeDetails, setShowPracticeDetails] = React.useState(false);
  const [isPracticeDisable, setIsPracticeDisable] = React.useState(false);
  const [isMacDisable, setIsMacDisable] = React.useState(false);
  const [showOptionPopup, setShowOptionsPopup] =
    React.useState<OptionPopupObjectProps>({ message: "", show: false });
  const [isEfctDateDisable, setIsEfctDateDisable] = React.useState(false);
  const [ maxInactiveDate, setMaxInactiveDate ] = React.useState(dayjs().add(20, 'year'));
  const [ minInactiveDate, setMinInactiveDate ] = React.useState(dayjs());
  const [ enabledDiscountList, setEnabledDiscountList ] = React.useState<string[]>([]);
  const [ enabledCustomPrice, setEnabledCustomPrice ] = React.useState<string[]>([]);
  const [ practiceData, setPracticeData] = React.useState<PracticeInfo | null>(null);
  const [ practiceProductData, setPracticeProductData] = React.useState<PracticeProductInfo[]>([]);
  const [productConfigByProductId, setProductConfigByProductId] = React.useState<Record<string, UpfrontPricingConfig>>({}); // CS-1845
  // CS-1764 - Late fee config by product ID
  const [lateFeeConfigByProductId, setLateFeeConfigByProductId] = React.useState<Record<string, LateFeeConfig>>({});
  const [isAddingGroupingProducts, setIsAddingGroupingProducts] = React.useState(false);

  const [isCollapsed, setIsCollapsed] = React.useState<number | null>(null);
  const [collapsedState, setCollapsedState] = React.useState<{ [key: number]: boolean }>({ 0: false });

  const [isCustomDiscountRadio, setCustomDiscountRadio] = React.useState(true);
  const [isCustomPriceRadio, setCustomPriceRadio] = React.useState(true);
  // CS-2070 - value each override field held when it gained focus, keyed by controller name.
  const overrideFocusValueRef = React.useRef<Record<string, string>>({});
  const pendingOverrideRevertRef = React.useRef<(() => void) | null>(null);
  const agreementRequestPrefillAppliedRef = React.useRef(false);
  const agreementRequestAutoProductAddedRef = React.useRef(false);
  const agreementRequestAutoAddAttemptedRef = React.useRef(false);
  const lastFetchedPracticeIdRef = React.useRef<number | null>(null);
  const inFlightPracticeFetchIdRef = React.useRef<number | null>(null);

  const handleCollapseToggle = (mainIndex: number) => {
    setCollapsedState((prev) => ({
      ...prev,
      [mainIndex]: !prev[mainIndex],
    }));
  };

  // CS-1845 START - Fetch and derive upfront pricing config for selected products
  const getUpfrontPricingFromRules = (rules: AgreementProductConfigRule[] = []): UpfrontPricingConfig => {
    const purchaseAndBillEnabled = rules.some((rule) =>
      rule.configType === PRODUCT_CONFIG_TYPE_PURCHASE_AND_BILL &&
      rule.configCode === PRODUCT_CONFIG_CODE_ENABLE &&
      rule.value === PRODUCT_CONFIG_VALUE_YES
    );
    const upfrontRule = rules.find((rule) =>
      rule.configType === PRODUCT_CONFIG_TYPE_UPFRONT_PRICING &&
      rule.configCode === PRODUCT_CONFIG_CODE_ENABLE
    );
    const discountValue = Number(upfrontRule?.metadata?.[UPFRONT_DISCOUNT_METADATA_KEY] ?? 0);

    return {
      enabled: purchaseAndBillEnabled && upfrontRule?.value === PRODUCT_CONFIG_VALUE_YES,
      upfrontDiscount: Number.isFinite(discountValue) ? discountValue : 0,
    };
  };

  // Grouping + Mandatory agreement mode check (disabled — keep for re-enable)
  // const isGroupingEligibleProduct = (rules: AgreementProductConfigRule[] = []): boolean => {
  //   const agreementMode = rules.find((rule) =>
  //     rule.configType === PRODUCT_CONFIG_TYPE_AGREEMENT &&
  //     rule.configCode === PRODUCT_CONFIG_CODE_MODE
  //   )?.value;
  //   return agreementMode === AGREEMENT_MODE_MANDATORY;
  // };

  // CS-1764 - Get late fee config from product config rules
  const getLateFeeFromRules = (rules: AgreementProductConfigRule[] = []): LateFeeConfig => {
    const lateFeeRule = rules.find((rule) =>
      rule.configType === PRODUCT_CONFIG_TYPE_LATE_FEE &&
      rule.configCode === PRODUCT_CONFIG_CODE_ENABLE
    );
    const lateFeeValue = Number(lateFeeRule?.metadata?.[LATE_FEE_PERCENT_METADATA_KEY] ?? 0);

    return {
      enabled: lateFeeRule?.value === PRODUCT_CONFIG_VALUE_YES,
      lateFeePercent: Number.isFinite(lateFeeValue) ? lateFeeValue : 0,
    };
  };

  const fetchUpfrontPricingConfig = async (productId: string | number, forceRefresh = false): Promise<UpfrontPricingConfig> => {
    const productKey = String(productId);
    if (!forceRefresh && productConfigByProductId[productKey]) {
      return productConfigByProductId[productKey];
    }

    try {
      const response = await getProductConfigByProduct(Number(productId));
      const rules: AgreementProductConfigRule[] = response?.data?.data || [];
      const upfrontConfig = getUpfrontPricingFromRules(rules);
      setProductConfigByProductId((prev) => ({
        ...prev,
        [productKey]: upfrontConfig,
      }));
      return upfrontConfig;
    } catch {
      setProductConfigByProductId((prev) => ({
        ...prev,
        [productKey]: { enabled: false, upfrontDiscount: 0 },
      }));
      return { enabled: false, upfrontDiscount: 0 };
    }
  };

  // CS-1764 - Fetch late fee config for a product
  const fetchLateFeeConfig = async (productId: string | number, forceRefresh = false): Promise<LateFeeConfig> => {
    const productKey = String(productId);
    if (!forceRefresh && lateFeeConfigByProductId[productKey]) {
      return lateFeeConfigByProductId[productKey];
    }

    try {
      const response = await getProductConfigByProduct(Number(productId));
      const rules: AgreementProductConfigRule[] = response?.data?.data || [];
      const lateFeeConfig = getLateFeeFromRules(rules);
      setLateFeeConfigByProductId((prev) => ({
        ...prev,
        [productKey]: lateFeeConfig,
      }));
      return lateFeeConfig;
    } catch {
      setLateFeeConfigByProductId((prev) => ({
        ...prev,
        [productKey]: { enabled: false, lateFeePercent: 0 },
      }));
      return { enabled: false, lateFeePercent: 0 };
    }
  };

  const getUpfrontPriceValue = (
    extendedPrice: string | number | null | undefined,
    allowableUnitPrice: string | number | null | undefined,
    upfrontDiscount?: number
  ) => {
    const numericExtendedPrice = typeof extendedPrice === "number" ? extendedPrice : parseAmount(extendedPrice || "");
    const numericAllowablePrice = typeof allowableUnitPrice === "number" ? allowableUnitPrice : parseAmount(allowableUnitPrice || "");
    const basePrice = numericExtendedPrice || numericAllowablePrice;
    if (!basePrice || !upfrontDiscount) return basePrice || 0;
    return calculateValue(String(upfrontDiscount), basePrice);
  };

  const upfrontPriceComponent = (row: ProductRow, showPercent = true): JSX.Element | string => {
    if (!row.upfrontPricingEnabled || row.extra) return "N/A";
    if (!row.upfrontDiscount) return "N/A";
    const upfrontPrice = getUpfrontPriceValue(row.extendedPrice, row.allowableUnitPrice, row.upfrontDiscount);
    if (!upfrontPrice) return "N/A";

    // showPercent=false next to the editable input: the "%" is already in the field, so show only the price.
    if (!showPercent) {
      return (
        <Box sx={{ display: "inline-flex", alignItems: "center", color: theme.palette.mode === "dark" ? "#5EF4C2" : "#047857", fontWeight: 700 }}>
          <BoltOutlined sx={{ fontSize: 16 }} />
          <Typography component="span" sx={{ color: theme.palette.mode === "dark" ? "#5EF4C2" : "#047857", fontWeight: 700, fontSize: "0.86rem" }}>
            ({priceFormatter(upfrontPrice)})
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, color: theme.palette.mode === "dark" ? "#5EF4C2" : "#047857", fontWeight: 700 }}>
        <BoltOutlined sx={{ fontSize: 16 }} />
        <Typography component="span" sx={{ color: theme.palette.mode === "dark" ? "#5EF4C2" : "#047857", fontWeight: 700, fontSize: "0.86rem" }}>
          {priceFormatter(upfrontPrice)}
        </Typography>
        <Typography component="span" sx={{ color: theme.palette.mode === "dark" ? "#D9FFF3" : "#334155", fontSize: "0.78rem", fontWeight: 600 }}>
          ({row.upfrontDiscount || 0}%)
        </Typography>
      </Box>
    );
  };

  const getUpfrontPriceForProductPrice = (productPriceId: string | number | null | undefined) => {
    const row = productRows.find((productRow) => String(productRow.productPriceId) === String(productPriceId));
    if (!row?.upfrontPricingEnabled) return null;
    // 0%/empty upfront discount -> no upfront pricing; send null.
    if (!row.upfrontDiscount) return null;

    const upfrontPrice = getUpfrontPriceValue(row.extendedPrice, row.allowableUnitPrice, row.upfrontDiscount);
    return upfrontPrice || null;
  };

  const getUpfrontDiscountForProductPrice = (productPriceId: string | number | null | undefined) => {
    const row = productRows.find((productRow) => String(productRow.productPriceId) === String(productPriceId));
    if (!row?.upfrontPricingEnabled) return null;
    if (!row.upfrontDiscount) return null;

    return row.upfrontDiscount;
  };

  // CS-2070 - Editable per-agreement Upfront Discount & Late Fee.
  const isValidPercent = (value: number | null | undefined): boolean =>
    value !== null && value !== undefined && !Number.isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100;

  const handleUpfrontDiscountChange = (productId: number | string, rawValue: string) => {
    const newValue = rawValue === "" ? undefined : Number(rawValue);
    // Inline validation: block non-numeric and out-of-range input while typing.
    if (newValue !== undefined && (Number.isNaN(newValue) || newValue < 0 || newValue > 100)) return;
    setProductRows((prevRows) =>
      prevRows.map((row) => (row.productId === productId ? { ...row, upfrontDiscount: newValue } : row))
    );
  };

  const handleLateFeePercentChange = (productId: number | string, rawValue: string) => {
    const newValue = rawValue === "" ? undefined : Number(rawValue);
    if (newValue !== undefined && (Number.isNaN(newValue) || newValue < 0 || newValue > 100)) return;
    setProductRows((prevRows) =>
      prevRows.map((row) => (row.productId === productId ? { ...row, lateFeePercent: newValue } : row))
    );
  };

  // Keep at most two decimal places without fighting the controlled input.
  const capToTwoDecimals = (raw: string): string => {
    if (!raw || !raw.includes(".")) return raw;
    const [intPart, decPart] = raw.split(".");
    return `${intPart}.${decPart.slice(0, 2)}`;
  };

  // CS-2070 - On blur, confirm an Upfront Discount % / Late Fee % override when the value changed this session;
  // the popup shows the product-config default (red) and the new value (green), and applies only on Confirm.
  const requestOverrideConfirmation = (
    field: "upfront" | "lateFee",
    controllerName: string,
    productName: string,
    defaultValue: number | undefined,
    applyChange: (rawValue: string) => void
  ) => {
    const focusRaw = (overrideFocusValueRef.current[controllerName] ?? "").trim();
    const currentRaw = String(getValues(controllerName as any) ?? "").trim();

    const currentNum = Number(currentRaw);
    if (currentRaw === "" || Number.isNaN(currentNum)) return;
    const focusNum = Number(focusRaw);
    if (focusRaw !== "" && !Number.isNaN(focusNum) && focusNum === currentNum) return;

    const defaultRaw = defaultValue === undefined || defaultValue === null ? "" : String(defaultValue).trim();
    const defaultNum = Number(defaultRaw);

    // Reverting to the product-config default is not an override - apply it without a confirmation.
    if (defaultRaw !== "" && !Number.isNaN(defaultNum) && defaultNum === currentNum) {
      applyChange(currentRaw);
      return;
    }

    const revert = () => setValue(controllerName as any, focusRaw, { shouldValidate: true });
    pendingOverrideRevertRef.current = revert;

    const fieldLabel = field === "upfront" ? "Upfront Discount %" : "Late Fee %";
    const isDark = theme.palette.mode === "dark";
    const fromColor = isDark ? "#F87171" : "#DC2626";
    const toColor = isDark ? "#5EF4C2" : "#059669";
    const fromDisplay = defaultRaw === "" ? "N/A" : `${defaultRaw}%`;
    const toDisplay = `${currentRaw}%`;

    setShowOptionsPopup({
      message: (
        <>
          You are changing the <strong>{fieldLabel}</strong> for <strong>{productName}</strong> from{" "}
          <span style={{ color: fromColor, fontWeight: 700 }}>{fromDisplay}</span> to{" "}
          <span style={{ color: toColor, fontWeight: 700 }}>{toDisplay}</span>. Do you want to continue?
        </>
      ),
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Confirm",
          color: "primary",
          onClick: () => {
            pendingOverrideRevertRef.current = null;
            applyChange(currentRaw);
            setShowOptionsPopup({ message: "", show: false });
          },
        },
        {
          name: "Cancel",
          color: "secondary",
          onClick: () => {
            pendingOverrideRevertRef.current = null;
            revert();
            setShowOptionsPopup({ message: "", show: false });
          },
        },
      ],
    });
  };

  // Compact, right-aligned percent editor with a trailing "%" adornment. The column header
  // already names the field, so no floating label is rendered inside the narrow table cell.
  const percentOverrideEditor = (
    field: "upfront" | "lateFee",
    controllerName: string,
    label: string,
    productName: string,
    defaultValue: number | undefined,
    onFieldChange: (rawValue: string) => void
  ): JSX.Element => {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "flex-start", ...tableFieldWrapSx(100) }}>
        <CustomTextField
          control={control}
          controllerName={controllerName}
          isNumberOnly
          size="small"
          label=""
          placeholder="0"
          onFocus={() => {
            overrideFocusValueRef.current[controllerName] = String(getValues(controllerName as any) ?? "");
          }}
          onChange={(e) => {
            const cleaned = capToTwoDecimals(e.target.value.replace(/[^0-9.]/g, ""));
            if (cleaned !== e.target.value) {
              setValue(controllerName as any, cleaned, { shouldValidate: true });
            }
          }}
          onBlur={() => {
            requestOverrideConfirmation(field, controllerName, productName, defaultValue, (rawValue) => onFieldChange(rawValue));
          }}
          rules={{
            required: `${label} is required. Set it to 0% to turn off ${field === "upfront" ? "upfront pricing" : "late fee"}.`,
            validate: (value: any) => {
              if (value === "" || value === null || value === undefined) return true;
              return isValidPercent(Number(value)) || "Enter a value between 0 and 100.";
            },
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{ inputMode: "decimal", "aria-label": label, style: { textAlign: "right" } }}
        />
      </Box>
    );
  };

  // Consistent "N/A" rendering for rows where the feature is not enabled.
  const naCell = (): JSX.Element => (
    <Typography component="span" sx={{ color: theme.palette.text.secondary, fontSize: "0.85rem" }}>
      N/A
    </Typography>
  );

  const upfrontDiscountEditorComponent = (row: ProductRow): JSX.Element => {
    if (!row.upfrontPricingEnabled) return naCell();
    return (
      // Percentage editor and the resulting Upfront Price sit on one line; the input has a
      // fixed width so the price can grow/shrink to the right without moving the field.
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
        {percentOverrideEditor(
          "upfront",
          `upfrontDiscountOverride.upfront${row.productId}`,
          "Upfront Discount %",
          row.name,
          row.defaultUpfrontDiscount,
          (rawValue) => handleUpfrontDiscountChange(row.productId, rawValue)
        )}
        {upfrontPriceComponent(row, false)}
      </Box>
    );
  };

  const lateFeeEditorComponent = (row: ProductRow): JSX.Element => {
    if (!row.lateFeeEnabled) return naCell();
    return percentOverrideEditor(
      "lateFee",
      `lateFeeOverride.lateFee${row.productId}`,
      "Late Fee %",
      row.name,
      row.defaultLateFeePercent,
      (rawValue) => handleLateFeePercentChange(row.productId, rawValue)
    );
  };

  // CS-1764 - Late fee component renderer
  const lateFeeComponent = (row: ProductRow): JSX.Element | string => {
    if (!row.lateFeeEnabled || row.extra) return "N/A";
    return (
      <Typography
        component="span"
        sx={{
          color: theme.palette.mode === "dark" ? "#FFAB00" : "#B45309",
          fontWeight: 700,
          fontSize: "0.86rem",
        }}
      >
        {row.lateFeePercent || 0}%
      </Typography>
    );
  };

  // CS-1764 - Get late fee percent for a product price
  const getLateFeePercentForProductPrice = (productPriceId: string | number | null | undefined) => {
    const row = productRows.find((productRow) => String(productRow.productPriceId) === String(productPriceId));
    if (!row?.lateFeeEnabled) return null;
    // 0%/empty late fee -> no late fee; send null.
    if (!row.lateFeePercent) return null;
    return row.lateFeePercent;
  };
  // CS-1845 END

  // Mounting
  useEffect(() => {
    const loadPracticeLookup = async () => {
      const practices = await dispatch(
        fetchPracticeLookupByUserRole({ activePracticeOnly: true })
      ).unwrap();

      const options: Option[] = (practices || [])
        .map((practice: any) => ({
          value: Number(practice.practiceId),
          label: String(practice.practiceName || ""),
        }))
        .filter((practice: Option) => Number.isFinite(Number(practice.value)));

      setPracticeLookupOptions(options);

      // CS-1745 START - Prefill practice from navigation state
      const practiceId = location.state?.practiceId;
      if (practiceId) {
        setValue("practiceName", practiceId);
        setValue("practiceId", practiceId);
        fetchPracticeInfoAndPracticeProducts();
        setIsPracticeDisable(false);
      }
      // CS-1745 END
    };

    const date = new Date();
    setValue("effectiveDate", moment(date).format("MM/DD/YYYY"));
    dispatch(fetchMac());
    dispatch(fetchManufacturersList());
    dispatch(fetchAllUserRoles());
    dispatch(fetchProductsByUserRoleData(true));
    loadPracticeLookup().catch(() => {
      const alertObj: SingleAlertInfo = {
        message: "Failed to load practices.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    });
  }, []);

  // CS-1747 START - Prefill Create Agreement flow from Agreement Request list
  useEffect(() => {
    if (agreementRequestPrefillAppliedRef.current) {
      return;
    }
    if (!openedFromAgreementRequestList) {
      return;
    }

    if (!Number.isFinite(requestedPracticeId)) {
      return;
    }
    if (!practiceLookupOptions || practiceLookupOptions.length === 0) {
      return;
    }

    const requestedPracticeExists = practiceLookupOptions.some(
      (practice) => Number(practice.value) === requestedPracticeId
    );
    if (!requestedPracticeExists) {
      return;
    }

    setValue("practiceName", requestedPracticeId as any);
    setValue("practiceId", requestedPracticeId as any);
    fetchPracticeInfoAndPracticeProducts(requestedPracticeId);
    setIsPracticeDisable(true);

    if (Number.isFinite(requestedProductId)) {
      setValue("product", requestedProductId as any);
      handleProductSelect(undefined, undefined, true);
    }

    agreementRequestPrefillAppliedRef.current = true;
  }, [openedFromAgreementRequestList, requestedPracticeId, requestedProductId, setValue, practiceLookupOptions, productsByUserRole]);

  useEffect(() => {
    if (agreementRequestAutoProductAddedRef.current || agreementRequestAutoAddAttemptedRef.current) {
      return;
    }
    if (!openedFromAgreementRequestList) {
      return;
    }

    if (!Number.isFinite(requestedProductId)) {
      agreementRequestAutoProductAddedRef.current = true;
      return;
    }

    const selectedProductId = Number(getValues("product"));
    if (!Number.isFinite(selectedProductId) || selectedProductId !== requestedProductId) {
      return;
    }

    if (!ProductPriceListForSingleProduct || ProductPriceListForSingleProduct.length === 0) {
      return;
    }

    const selectedProductExists = (productsByUserRole || []).some(
      (prod) => Number(prod.productId) === requestedProductId
    );
    if (!selectedProductExists) {
      return;
    }

    agreementRequestAutoAddAttemptedRef.current = true;
    verifyAddProduct().then((isAdded) => {
      if (isAdded) {
        agreementRequestAutoProductAddedRef.current = true;
      }
    });
  }, [openedFromAgreementRequestList, requestedProductId, ProductPriceListForSingleProduct, getValues, productsByUserRole]);
  // CS-1747 END


  const discountComponent = (
    prod: ProductInfo | undefined,
    allowableUnitPrice: any,
    customPrice: any,
    disableUnit?: string,
    productPriceId?: string
  ): JSX.Element => {
    
    return (
      <>
        <Grid container spacing={1} justifyContent='center' alignItems="center">
          <Grid item>
              <CustomRadioButton
                control={control}
                controllerName={`radio.discount${productPriceId}`}
                options={radioButtonOptions(prod, allowableUnitPrice, customPrice, disableUnit, productPriceId)}
                onChange={(e) => handleDiscount(prod, allowableUnitPrice, customPrice, e.target.value, productPriceId)}
                radioSx={tableRadioSx}
                radioLabelSx={tableRadioLabelSx}
              />
          </Grid>
        </Grid>
      </>
    );
  }

  const radioButtonOptions = (prod: ProductInfo | undefined, allowableUnitPrice: any, customPrice: number | null, disableUnit?:string, productPriceId?: string) => {
    const options: { value: string | number; label: string | JSX.Element }[] = []
    //CS-1337 START
    if (customPrice === null || customPrice === undefined) {
      options.push({
        label: (
          <Box sx={clickToSelectFieldWrapSx(140)} onClick={focusFieldAfterSelect}>
            <CustomTextField
              control={control}
              controllerName={`discount.discount${productPriceId}`}
              label=""
              placeholder="Discount %"
              onChange={(e) =>
                setDiscountOnFieldChange(prod, allowableUnitPrice, e.target.value, productPriceId)}
              type="number"
              isDisabled={!!(!disableUnit || disableUnit === "customDiscount")}
              onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  const value = target.value;
                  if (value.includes('.')) {
                    const [intPart, decPart] = value.split('.');
                    if (decPart.length > 2) {
                      target.value = `${intPart}.${decPart.slice(0, 2)}`;
                    }
                  }
                }}
              rules={{
                min: { value: 0, message: "Minimum value allowed is 0" },//CS-1530
                max: { value: 100, message: "Maximum value allowed is 100" }
              }}
              size='small'
            />
          </Box>
        ),
        value: `customDiscount`
      });
    }

    const isCustomPriceDisabled = Boolean((customPrice !== null && customPrice !== undefined) || (disableUnit && disableUnit === "customPrice"));
    
    options.push({
      label: (
        <Box
          sx={clickToSelectFieldWrapSx(140)}
          onClick={(e) => {
            if (!isCustomPriceDisabled && getValues(`radio.discount${productPriceId}` as any) !== "customPrice") {
              setValue(`radio.discount${productPriceId}` as any, "customPrice");
              handleDiscount(prod, allowableUnitPrice, customPrice, "customPrice", productPriceId);
            }
            focusFieldAfterSelect(e);
          }}
        >
          <CustomCurrencyField
            control={control}
            controllerName={`customPrice.customPrice${productPriceId}`}
            label=""
            placeholder="Custom Price"
            onChange={(e) =>
              e?.target?.value && setCustomPriceChange(prod, allowableUnitPrice, e?.target?.value, customPrice, productPriceId)}
            isDisabled={isCustomPriceDisabled}
            size='small'
          />
        </Box>
      ),
        value: 'customPrice'
      });

    return options;
  }
  //CS-1337 END

  const closeComponent = (productPriceId: any): JSX.Element => {
    return (
      <>
        <FormControl>
          <CustomIconButton 
            color="error"
            size="medium"
            variant="outlined"
            onClick={() => handleClose(productPriceId)}
            sx={tableDeleteBtnSx}
          >
            <DeleteOutlineOutlined fontSize="small" />
          </CustomIconButton>
        </FormControl>
      </>
    );
  };

  const handleClose = (productPriceId: any) => {
    let products: PracticeProductInfo[] | null = getValues("practiceProducts");
    const practiceId = getValues("practiceId");
    const macId = getValues("macId");
    const effectiveDate = dayjs(getValues("effectiveDate"));
    products = products?.filter(
      (product) => productPriceId !== product.productPriceId
    );
    deleteDiscountComponentRow(productPriceId);
    delete tierPriceBeforeSelectRef.current[String(productPriceId)];
    setProductRows(prevRows => {
      const newRows = prevRows.filter((row) => row.productPriceId !== productPriceId);
      return newRows;
    });
    setValue("practiceProducts", products);
    if (products?.length > 0) {
      setIsPracticeDisable(!!practiceId);
      setIsMacDisable(!!macId);
      setIsEfctDateDisable(effectiveDate.isValid() ?? false);
    } else {
      setIsPracticeDisable(false);
      setIsEfctDateDisable(false);
      setIsMacDisable(false);
    }
  };

  const releaseTierChoice = (productPriceId?: string | number) => {
    if (productPriceId === undefined || productPriceId === null || productPriceId === "") {
      return;
    }
    delete tierPriceBeforeSelectRef.current[String(productPriceId)];
    setValue(`tierPrice.tier${productPriceId}` as any, "");
  };

  const setDiscount = (    
    allowableUnitPrice: any,
    discount: string,
    products: PracticeProductInfo[],
    productPriceId?: string) => {
      releaseTierChoice(productPriceId);
      products?.map((product) => {
        if (product.productPriceId === productPriceId) {
          product.discountType = discount;
          product.extendedPrice = calculateValue(discount, allowableUnitPrice) || null;
        }
      }); 
      setProductRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.productPriceId === productPriceId) {
            return {
              ...row,
                extendedPrice: allowableUnitPrice && discount ? priceFormatter(calculateValue(discount, allowableUnitPrice)) : null,
                discount: discountComponent(undefined, allowableUnitPrice, row.customPrice, "customPrice",productPriceId )
            };
          }
          return row;
        });
      });
      return products;
  };

  const setCustomPrice = (    
    allowableUnitPrice: any,
    value: number,
    products: PracticeProductInfo[],
    customPrice:number | null,
    productPriceId?: string,
    ) => {
      releaseTierChoice(productPriceId);
      products?.map((product) => {
        if (product.productPriceId === productPriceId) {
          product.discountType = "CUSTOM";
          product.extendedPrice =  value || null;
        }
      }); 
      setProductRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.productPriceId === productPriceId) {
            return {
              ...row,
              extendedPrice: priceFormatter(value) || null,
              discount: discountComponent(undefined, allowableUnitPrice, row.customPrice, "customDiscount",productPriceId )
            };
          }
          return row;
        });
      });
      return products;
  };

  const setDiscountOnFieldChange = (
    prod: ProductInfo | undefined,
    allowableUnitPrice: any,
    discount: string,
    productPriceId?: string
  ) => {

      let products: PracticeProductInfo[] = getValues("practiceProducts");
      products = setDiscount(allowableUnitPrice, discount, products, productPriceId)
      setValue("practiceProducts", products);
  };

  const convertToFloat = (str: string) => {
    const cleanedStr = str && str.replace(/,/g, "");
    return parseFloat(cleanedStr);
  };

  const setCustomPriceChange = (
    prod: ProductInfo | undefined,
    allowableUnitPrice: any,
    value: string,
    customPrice:number | null,
    productPriceId?: string,
  ) => {
    let products: PracticeProductInfo[] = getValues("practiceProducts");
    products = setCustomPrice(allowableUnitPrice, convertToFloat(value), products, customPrice, productPriceId)
    setValue("practiceProducts", products);
  };

  const deleteDiscountComponentRow = (productPriceId?: string) => {
    let discounts = getValues("discount");
    let customPriceOpt = getValues('customPrice');
    let radio = getValues('radio');
    // @ts-ignore
    delete discounts[`discount${productPriceId}`];
    // @ts-ignore
    delete radio[`discount${productPriceId}`];
    // @ts-ignore
    delete customPriceOpt[`customPrice${productPriceId}`];
    setValue("discount", discounts);
    setValue("radio", radio);
    setValue("customPrice", customPriceOpt);
  };

  const handleDiscount = (    
    prod: ProductInfo | undefined,
    allowableUnitPrice: any,
    customPrice: number | null,
    type: string,
    productPriceId?: string) => {
      releaseTierChoice(productPriceId);
      let products: PracticeProductInfo[] | null = getValues("practiceProducts");
      //@ts-ignore
      const discount: number | string = getValues(`discount.discount${productPriceId}`) ? getValues(`discount.discount${productPriceId}`) : "";
      if(type === 'customDiscount') {
        products = setDiscount(allowableUnitPrice, discount.toString() , products, productPriceId);
        //@ts-ignore
        setValue(`customPrice.customPrice${productPriceId}`, "");
        setProductRows((prevRows) => {
          return prevRows.map((row) => {
            if (row.productPriceId === productPriceId) {
              return {
                ...row,
                extendedPrice: priceFormatter(allowableUnitPrice),
                discount: discountComponent(prod, allowableUnitPrice, customPrice, "customPrice", productPriceId)
              };
            }
            return row;
          });
        });
      } else {
        //@ts-ignore
        setValue(`customPrice.customPrice${productPriceId}`, customPrice)
        products?.map((product) => {
          if (product.productPriceId === productPriceId) {
            //@ts-ignore
            product.extendedPrice = getValues(`customPrice.customPrice${productPriceId}`);
            product.discountType = "CUSTOM";
          }
        });
        setProductRows((prevRows) => {
          return prevRows.map((row) => {
            if (row.productPriceId === productPriceId) {
              return {
                ...row,
                //@ts-ignore
                extendedPrice: priceFormatter(getValues(`customPrice.customPrice${productPriceId}`)),
                discount: discountComponent(prod, allowableUnitPrice, customPrice, "customDiscount", productPriceId)
              };
            }
            return row;
          });
        });
        //@ts-ignore
        setValue(`discount.discount${productPriceId}`, "")
      };
      setValue("practiceProducts", products);
  } 

  const handleChangePracticeMacDropDown = (event: any, value: any) => {
    setValue("macId", value);

    setValue("practiceProducts", []);
    setProductRows([])
  }

  const handleChangeManufacturer = (_event: any, value: any) => {
    setValue("manufacturerId", value);
    setValue("product", null);
    setValue("groupingProductIds", []);
    // Selected Products table is cumulative — do not clear when switching manufacturer.

    const selectedManufacturer = manufacturerInfoList?.find(
      (manufacturer) => Number(manufacturer.manufacturerId) === Number(value)
    );
    if (isGroupingEnabledManufacturer(selectedManufacturer)) {
      setValue("groupingChecked", true);
      setValue("agreementMode", "GROUPING");
    } else {
      setValue("groupingChecked", false);
      setValue("agreementMode", "SINGLE");
    }
  }

  const handleChangePracticeName = (event: any, value: any) => {
    let products: PracticeProductInfo[] = getValues("practiceProducts");
    setValue("practiceId", value);

    if (getValues("practiceId")) {
      fetchPracticeInfoAndPracticeProducts(value);
    }

    if (products.length > 0) {
      setIsPracticeDisable(true);
    }
  };

  const handleAgreementModeChange = () => {
    setValue("practiceProducts", []);
    setProductRows([]);
    setValue("product", null);
  };
  let errorObj: SingleAlertInfo = {
    message: ``,
    alertType: "error",
  };

  const getEffectivePracticeProduct = (id: string) => {
    const selectedProduct = productsByUserRole.find(
      (prod) => prod.productId === id
    );
    const productPrices = selectedProduct?.productPrices;
    const effectiveDate = dayjs(getValues("effectiveDate"));
    let validProductPrice: ProductPrice | null = null;
    if (productPrices && effectiveDate.isValid()) {
      for (let i = 0; i < productPrices.length; i++) {
        const productPriceEffective = dayjs(productPrices[i].effectiveDate);
        // CS-633 Stage changes for stage error
        const productPriceInactive = dayjs(productPrices[i].inactiveDate);
        //
        if (
        // CS-633 Stage changes for stage error
          (!productPriceInactive.isValid() ||
           (effectiveDate.isBefore(productPriceInactive)) || (effectiveDate.isSame(productPriceInactive)) ) &&
           //
          ( effectiveDate.isAfter(productPriceEffective) || effectiveDate.isSame(productPriceEffective) )
        ) {
          validProductPrice = productPrices[i];
        }
      }
    }
    return validProductPrice;
  };

  const tierOptionsForPrice = (tierPrices?: Record<string, number | string> | null) => {
    if (!tierPrices || typeof tierPrices !== "object") {
      return [];
    }
    const entries = Object.entries(tierPrices).filter(([, value]) => {
      return value !== null && value !== undefined && String(value).trim() !== "";
    });
    const orderedKeys = [
      ...tierCodeRows.map((row) => row.keyName).filter((key) => entries.some(([entryKey]) => entryKey === key)),
      ...entries.map(([key]) => key).filter((key) => !tierCodeRows.some((row) => row.keyName === key)),
    ];
    return orderedKeys.map((key) => {
      const amount = Number(tierPrices[key]);
      const tierName = tierCodeRows.find((row) => row.keyName === key)?.displayValue || key;
      return {
        value: key,
        label: `${tierName} - ${priceFormatter(amount)}`,
        amount,
      };
    }).filter((option) => !Number.isNaN(option.amount));
  };

  const applyTierSelection = (productPriceId: string | number, amount: number) => {
    const key = String(productPriceId);
    const products = getValues("practiceProducts") || [];
    const currentProduct = products.find((product) => String(product.productPriceId) === key);
    const rawExtendedPrice = currentProduct?.extendedPrice;
    const previousExtendedPrice = typeof rawExtendedPrice === "number" ? rawExtendedPrice : null;
    const currentRadio = getValues(`radio.discount${productPriceId}` as any);
    setProductRows((prevRows) =>
      prevRows.map((row) => {
        if (String(row.productPriceId) !== key) {
          return row;
        }
        if (!tierPriceBeforeSelectRef.current[key]) {
          tierPriceBeforeSelectRef.current[key] = {
            extendedPrice: previousExtendedPrice,
            extendedPriceDisplay: row.extendedPrice ?? null,
            appliedAmount: amount,
            discountType: currentProduct?.discountType,
            radio: currentRadio ? String(currentRadio) : "",
          };
        } else {
          tierPriceBeforeSelectRef.current[key].appliedAmount = amount;
        }
        return { ...row, extendedPrice: priceFormatter(amount) };
      })
    );
    products.forEach((product) => {
      if (String(product.productPriceId) === key) {
        product.extendedPrice = amount;
        product.discountType = "CUSTOM";
      }
    });
    setValue("practiceProducts", products);
    setValue(`radio.discount${productPriceId}` as any, "");
  };

  const clearTierSelection = (productPriceId: string | number) => {
    const key = String(productPriceId);
    const saved = tierPriceBeforeSelectRef.current[key];
    delete tierPriceBeforeSelectRef.current[key];
    if (!saved) {
      return;
    }
    const products = getValues("practiceProducts") || [];
    const currentProduct = products.find((product) => String(product.productPriceId) === key);
    if (currentProduct?.extendedPrice !== saved.appliedAmount) {
      return;
    }
    setProductRows((prevRows) =>
      prevRows.map((row) =>
        String(row.productPriceId) === key
          ? { ...row, extendedPrice: saved.extendedPriceDisplay }
          : row
      )
    );
    products.forEach((product) => {
      if (String(product.productPriceId) === key) {
        product.extendedPrice = saved.extendedPrice;
        product.discountType = saved.discountType;
      }
    });
    setValue("practiceProducts", products);
    setValue(`radio.discount${productPriceId}` as any, saved.radio || "");
  };

  const tierPriceCell = (productPriceId: string | number, tierPrices?: Record<string, number | string> | null) => {
    const options = tierOptionsForPrice(tierPrices);
    if (options.length === 0) {
      return "-";
    }
    return (
      <Box
        sx={{
          ...dropdownWrapSx(),
          minWidth: 180,
          "& .MuiInputBase-input::placeholder": {
            color: mTokens.textSecondary,
            opacity: 1,
          },
        }}
      >
        <CustomDropdownField
          control={control}
          name={`tierPrice.tier${productPriceId}`}
          options={options.map(({ value, label }) => ({ value, label }))}
          label=""
          size="small"
          placeholderText="Select tier price"
          sortOptions={false}
          onChangeSelect={(_event: any, value: string) => {
            if (!value) {
              clearTierSelection(productPriceId);
              return;
            }
            const selected = options.find((option) => option.value === value);
            if (selected) {
              applyTierSelection(productPriceId, selected.amount);
            }
          }}
        />
      </Box>
    );
  };


  const verifyAddProduct = async (): Promise<boolean> => {
    if ((getValues("groupingProductIds") || []).length > 0) {
      return addSelectedProducts();
    }
    if (getValues("agreementMode") === "GROUPING") {
      const selectedManufacturer = manufacturerInfoList?.find(
        (manufacturer) => Number(manufacturer.manufacturerId) === Number(getValues("manufacturerId"))
      );
      if (!isGroupingEnabledManufacturer(selectedManufacturer)) {
        errorObj.message = "Grouping is allowed only when manufacturer Agreement Grouping is enabled.";
        dispatch(setSingleAlertObj(errorObj));
        return false;
      }
      const selectedProductId = getValues("product");
      if (!selectedProductId) {
        errorObj.message = "Please select a Product";
        dispatch(setSingleAlertObj(errorObj));
        return false;
      }
      // Grouping + Mandatory agreement mode check (disabled — keep for re-enable)
      // try {
      //   const response = await getProductConfigByProduct(Number(selectedProductId));
      //   const rules: AgreementProductConfigRule[] = response?.data?.data || [];
      //   if (!isGroupingEligibleProduct(rules)) {
      //     errorObj.message = "Grouping is allowed only when the product is Mandatory agreement mode.";
      //     dispatch(setSingleAlertObj(errorObj));
      //     return false;
      //   }
      // } catch {
      //   errorObj.message = "Unable to verify product configuration for grouping.";
      //   dispatch(setSingleAlertObj(errorObj));
      //   return false;
      // }
    }
    if (practiceData?.billingType === "INDIVIDUAL") {
      const selectedProductId = getValues("product");
      const selectedMacId = getValues("macId");
      const associatedProviders: number[] = practiceProductData
        .filter(product => product.productId === selectedProductId && product.macId === selectedMacId)
        .map(product => product.practiceProviderId)
        .filter((practiceProviderId): practiceProviderId is number => practiceProviderId !== null);

      const allProviders = practiceData?.practiceProviders || [];

      const providersAssociatedWithProduct = allProviders.filter(provider =>
        associatedProviders.includes(provider.providerId)
      );

      const providersToBeAssociated = allProviders.filter(
        provider => !associatedProviders.includes(provider.providerId)
      );

      const getProviderName = (provider: any) => {
        const title = provider.title ? provider.title + " " : "";
        const fullName = `${title}${provider.firstName} ${provider.lastName}`.trim();
        return fullName;
      };

      const providerNamesAssociatedWithProduct = providersAssociatedWithProduct.map(getProviderName);
      const providerNamesToBeAssociated = providersToBeAssociated.map(getProviderName);

      if (providerNamesToBeAssociated.length == 0) {
        setShowOptionsPopup({
          message: "Agreement(s) for this product already exist for every provider.",
          show: true,
          variant: "warning",
          buttons: [
            {
              name: "Cancel",
              color: "secondary",
              onClick: () => {
                cancelOptionPopup()
              },
            },
          ],
        });
        return false;
      } else if (providerNamesAssociatedWithProduct.length == 0) {
        return await addProduct();
      } else if (providersAssociatedWithProduct.length != 0 && providerNamesToBeAssociated.length != 0) {
        let providersMessage = ``;
        providersMessage += `Agreement(s) for this product are already available for:\n${providerNamesAssociatedWithProduct.join("\n ")}\n\n`;

        providersMessage += `New agreement(s) will be created for:
          ${providerNamesToBeAssociated.join("\n ")}\n\n`;

        providersMessage += `Do you want to add this product?`;

        setShowOptionsPopup({
          message: providersMessage,
          show: true,
          variant: "info",
          buttons: [
            {
              name: "Confirm",
              color: "primary",
              onClick: () => {
                addProduct();
                setShowOptionsPopup({ message: "", show: false });
              },
            },
            {
              name: "Cancel",
              color: "secondary",
              onClick: () => {
                cancelOptionPopup()
              },
            },
          ],
        });
        return false;
      }
    } else {
      return await addProduct();
    }
    return false;
  }
  
    const handleProductSelect = (_event?: any, selectedValue?: string | number | null, useCurrentValue = false) => {
    const effectiveDate = getValues('effectiveDate');
    dispatch(updateElementInProductListSlice({
      elementName: "ProductPriceListForSingleProduct",
      value: []
    }));
    const productId = selectedValue || (useCurrentValue ? getValues('product') : "");
    const productPriceByProdIdObj : ProductPriceByProductRequest = {
      productId: productId ? Number(productId) : 0,
      effectiveDate : effectiveDate && dayjs(effectiveDate).isValid() ? effectiveDate : dayjs().format("MM/dd/yyyy")
    }
    if (productId) {
      dispatch(getProductPriceByProductId(productPriceByProdIdObj));
      void fetchUpfrontPricingConfig(productId, true); // CS-1845
      void fetchLateFeeConfig(productId, true); // CS-1764
    }
  }

  const checkProductPriceValidity = (pp: ProductPrice) => {
    let selectedMacId = getValues("macId");
    if(selectedMacId && pp.macId === selectedMacId) {
      return true;
    } else if(selectedMacId && pp.macId !== selectedMacId) {
      return false;
    } else if( !selectedMacId && parseInt(pp.macId) > 0) {
      return false;
    } else {
      return true;
    }
  };

  const addProduct = async (
    productIdParam?: string,
    priceListParam?: ProductPrice[],
    configParam?: { upfrontPricingConfig: UpfrontPricingConfig; lateFeeConfig: LateFeeConfig }
  ): Promise<boolean> => {
    let products: PracticeProductInfo[] = getValues("practiceProducts") || [];
    const selectedProductId = productIdParam ?? getValues("product");
    const addingProduct = productRows.find(
      (prod) => prod.productId === selectedProductId
    );
    const effectiveDate = dayjs(getValues("effectiveDate"));
    if (!selectedProductId) {
      errorObj.message = "Please select a Product";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    } else if (addingProduct) {
      errorObj.message = "The product has already been added.";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    } else if (!effectiveDate || !effectiveDate.isValid()) {
      errorObj.message = "Please select a valid date.";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    } else {
      if (!addingProduct) {
        let selectedProduct = productsByUserRole?.find(
          (prod) => prod.productId === selectedProductId
        );
        if (selectedProduct) {
          const upfrontPricingConfig = configParam?.upfrontPricingConfig ?? await fetchUpfrontPricingConfig(selectedProduct.productId); // CS-1845
          const lateFeeConfig = configParam?.lateFeeConfig ?? await fetchLateFeeConfig(selectedProduct.productId); // CS-1764
          
          let rows: any[] = [];
          (priceListParam ?? ProductPriceListForSingleProduct)?.forEach((pp: ProductPrice) => {
          const valid = checkProductPriceValidity(pp);
           if(selectedProduct && valid) {
            //CS-1337 START
            const shouldPreselectCustomPrice = pp.customPrice !== null && pp.customPrice !== undefined;
            
            products.push({
              productId: selectedProduct.productId,
              extendedPrice: shouldPreselectCustomPrice ? pp.customPrice : null,
              productPriceId: pp.productPriceId,
              productItemId: pp.productItem?.productItemId,
              practiceProviderId: null,
              macId: pp.macId,
              discountType: shouldPreselectCustomPrice ? "CUSTOM" : undefined,
              customPrice: pp.customPrice
            });
            
            let row = {
              productId: selectedProduct.productId,
              name: selectedProduct?.productName,
              discount: discountComponent(
                selectedProduct,
                pp.allowableUnitPrice,
                pp.customPrice,
                shouldPreselectCustomPrice ? "customDiscount" : undefined,
                pp.productPriceId
              ),
              price: pp.allowableUnitPrice ? parseInt(pp.macId ) > 0 ? priceFormatter(pp.allowableUnitPrice) + " (Local)" : priceFormatter(pp.allowableUnitPrice) + " (National)" : "No Local or National Fee",
              extendedPrice: shouldPreselectCustomPrice ? priceFormatter(pp.customPrice) : null,
              productPriceId: pp.productPriceId,
              productItemId: pp.productItem?.productItemId,
              sku: pp.productItem?.sku,
              uniqueId: pp.productPriceId,
              allowableUnitPrice : pp.allowableUnitPrice,
              customPrice: pp.customPrice,
              tier: tierPriceCell(pp.productPriceId, pp.tierPrices),
              upfrontPricingEnabled: upfrontPricingConfig.enabled, // CS-1845
              upfrontDiscount: upfrontPricingConfig.upfrontDiscount, // CS-1845
              defaultUpfrontDiscount: upfrontPricingConfig.upfrontDiscount, // CS-2070 - default kept for override detection
              lateFeeEnabled: lateFeeConfig.enabled, // CS-1764
              lateFeePercent: lateFeeConfig.lateFeePercent, // CS-1764
              defaultLateFeePercent: lateFeeConfig.lateFeePercent, // CS-2070 - default kept for override detection
            }
            rows.push(row);
            if (shouldPreselectCustomPrice) {
              setValue(`radio.discount${pp.productPriceId}` as any, "customPrice");
              setValue(`customPrice.customPrice${pp.productPriceId}` as any, priceFormatter(pp.customPrice));
              setValue(`discount.discount${pp.productPriceId}` as any, "");
              setEnabledCustomPrice(prev => {
                const newVal = [...prev, `customPrice.customPrice${pp.productPriceId}`];
                return newVal;
              });
              setEnabledDiscountList(prev => {
                const newVal = prev.filter(discount => discount !== `discount.discount${pp.productPriceId}`);
                return newVal;
              });
            }
            //CS-1337 END
           }
          });
          if (rows.length === 0) {
            dispatch(setSingleAlertObj({ ...errorObj, message: "No Local or National fee for the selected MAC." }));
            return false;
          }
          setProductRows(prevRows => {
            let newRows = [...prevRows, ...rows]
            return newRows;
          });
          setValue("practiceProducts", products);
          // CS-2070 - Pre-populate the editable override fields with the product-config defaults.
          if (upfrontPricingConfig.enabled) {
            setValue(`upfrontDiscountOverride.upfront${selectedProduct.productId}` as any, upfrontPricingConfig.upfrontDiscount ?? 0);
          }
          if (lateFeeConfig.enabled) {
            setValue(`lateFeeOverride.lateFee${selectedProduct.productId}` as any, lateFeeConfig.lateFeePercent ?? 0);
          }
          return rows.length > 0;
          
        }
      }
    }
    const practiceId = getValues("practiceId");
    let selectedMacId = getValues("macId");
    if (products.length > 0) {
      if (practiceId) {
        setIsPracticeDisable(true);
      }
      if (dayjs(getValues("effectiveDate")).isValid()) {
        setIsEfctDateDisable(true);
      }
      if(selectedMacId) {
        setIsMacDisable(true);
      }
    }
    return false;
  };

  const isProductCoveredForAllProviders = (productId: string | number): boolean => {
    if (practiceData?.billingType !== "INDIVIDUAL") {
      return false;
    }
    const selectedMacId = getValues("macId");
    const associatedProviders = practiceProductData
      .filter(product => String(product.productId) === String(productId) && product.macId === selectedMacId)
      .map(product => product.practiceProviderId)
      .filter((practiceProviderId): practiceProviderId is number => practiceProviderId !== null);
    const allProviders = practiceData?.practiceProviders || [];
    return allProviders.every(provider => associatedProviders.includes(provider.providerId));
  };

  const handleGroupingProductsChange = (_event: any, valueArray: (string | number)[]) => {
    const allProductIds = productOptions.map((option) => option.value);
    const previousHadAll = (watchedGroupingProductIds || []).includes(ALL_GROUPING_PRODUCTS_VALUE);
    const hasAll = valueArray.includes(ALL_GROUPING_PRODUCTS_VALUE);
    const selectedProductIds = valueArray.filter((value) => value !== ALL_GROUPING_PRODUCTS_VALUE);
    let nextValue: (string | number)[];
    if (hasAll && !previousHadAll) {
      nextValue = [...allProductIds, ALL_GROUPING_PRODUCTS_VALUE];
    } else if (!hasAll && previousHadAll && selectedProductIds.length === allProductIds.length) {
      nextValue = [];
    } else if (allProductIds.length > 0 && selectedProductIds.length === allProductIds.length) {
      nextValue = [...selectedProductIds, ALL_GROUPING_PRODUCTS_VALUE];
    } else {
      nextValue = selectedProductIds;
    }
    setValue("groupingProductIds", nextValue);
  };

  const addSelectedProducts = async (): Promise<boolean> => {
    const selectedManufacturer = manufacturerInfoList?.find(
      (manufacturer) => Number(manufacturer.manufacturerId) === Number(getValues("manufacturerId"))
    );
    if (getValues("agreementMode") === "GROUPING" && !isGroupingEnabledManufacturer(selectedManufacturer)) {
      errorObj.message = "Grouping is allowed only when manufacturer Agreement Grouping is enabled.";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    }
    const effectiveDateValue = getValues("effectiveDate");
    if (!effectiveDateValue || !dayjs(effectiveDateValue).isValid()) {
      errorObj.message = "Please select a valid date.";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    }

    const selectedProductIds = new Set(
      (getValues("groupingProductIds") || [])
        .filter((value) => value !== ALL_GROUPING_PRODUCTS_VALUE)
        .map((value) => String(value))
    );
    const addedProductIds = new Set(productRows.map((row) => String(row.productId)));
    const candidates = productOptions
      .filter((option) => selectedProductIds.has(String(option.value)) && !addedProductIds.has(String(option.value)))
      .map((option) => productsByUserRole.find((prod) => String(prod.productId) === String(option.value)))
      .filter((prod): prod is ProductInfo => !!prod);

    if (candidates.length === 0) {
      errorObj.message = "The selected product(s) have already been added.";
      dispatch(setSingleAlertObj(errorObj));
      return false;
    }

    setIsAddingGroupingProducts(true);
    try {
      const fetchedProducts = await Promise.all(candidates.map(async (selectedProduct) => {
        if (isProductCoveredForAllProviders(selectedProduct.productId)) {
          return null;
        }
        const [priceList, rules] = await Promise.all([
          productPriceByProductId({ productId: Number(selectedProduct.productId), effectiveDate: effectiveDateValue })
            .then((response) => (response?.data?.data || []) as ProductPrice[])
            .catch(() => [] as ProductPrice[]),
          getProductConfigByProduct(Number(selectedProduct.productId))
            .then((response) => (response?.data?.data || []) as AgreementProductConfigRule[])
            .catch(() => [] as AgreementProductConfigRule[]),
        ]);
        return {
          priceList,
          config: {
            upfrontPricingConfig: getUpfrontPricingFromRules(rules), // CS-1845
            lateFeeConfig: getLateFeeFromRules(rules), // CS-1764
          },
        };
      }));

      const noFeeProducts: string[] = [];
      const coveredProducts: string[] = [];
      let addedCount = 0;
      for (let i = 0; i < candidates.length; i++) {
        const fetchedProduct = fetchedProducts[i];
        if (fetchedProduct === null) {
          coveredProducts.push(candidates[i].productName);
          continue;
        }
        if (await addProduct(candidates[i].productId, fetchedProduct.priceList, fetchedProduct.config)) {
          addedCount++;
        } else {
          noFeeProducts.push(candidates[i].productName);
        }
      }

      if (noFeeProducts.length > 0 || coveredProducts.length > 0) {
        const messages: string[] = [];
        if (noFeeProducts.length > 0) {
          messages.push(`No Local or National fee for the selected MAC: ${noFeeProducts.join(", ")}`);
        }
        if (coveredProducts.length > 0) {
          messages.push(`Agreement(s) already exist for every provider: ${coveredProducts.join(", ")}`);
        }
        dispatch(setSingleAlertObj({
          message: messages.join(". "),
          alertType: "error",
        }));
      }
      return addedCount > 0;
    } finally {
      setIsAddingGroupingProducts(false);
    }
  };

  const onSubmit = (data: any) => {
    const {
      discount,
      tierPrice,
      product,
      practiceMacDropDown,
      close,
      practiceName,
      extendedValues,
      customPrice,
      radio,
      upfrontDiscountOverride, // CS-2070 - form-only editor fields, not part of the payload
      lateFeeOverride, // CS-2070
      agreementMode: selectedAgreementMode,
      groupingProductIds,
      ...agreementObj
    } = data;
    // CS-1845 - Include calculated upfront price in payload using the latest extended/allowable price.
    // CS-1764 - Include late fee percent in payload
    const agreementObjWithUpfrontPrice = {
      ...agreementObj,
      grouping: selectedAgreementMode === "GROUPING",
      practiceProducts: agreementObj.practiceProducts?.map((practiceProduct: PracticeProductInfo) => ({
        ...practiceProduct,
        upfrontPrice: getUpfrontPriceForProductPrice(practiceProduct.productPriceId),
        upfrontDiscount: getUpfrontDiscountForProductPrice(practiceProduct.productPriceId),
        lateFeePercent: getLateFeePercentForProductPrice(practiceProduct.productPriceId), // CS-1764
      })),
    };
  
      if( !dayjs(data.effectiveDate).isValid()) {
        let errorObj: SingleAlertInfo = {
          message: "Please select valid effective date.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj));
      } else {
        if (agreementObjWithUpfrontPrice.practiceProducts?.length === 0) {
          let errorObj: SingleAlertInfo = {
            message: "Please select atleast one product.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errorObj));
        } else if (
          selectedAgreementMode === "GROUPING" &&
          new Set(
            agreementObjWithUpfrontPrice.practiceProducts.map((prod: any) => Number(prod.productId))
          ).size < 2
        ) {
          let errorObj: SingleAlertInfo = {
            message: "Grouping requires at least 2 products.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errorObj));
        } else if (
          agreementObjWithUpfrontPrice.practiceProducts.find(
            (prod: any) => prod.discountType === undefined 
            || (prod.discountType === '' || prod.discountType === undefined)
            || (prod.extendedPrice === null || isNaN(prod.extendedPrice))
          )
        ) {
          let errorObj: SingleAlertInfo = {
            message: "Please select a discount or enter a custom price for each added product.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errorObj));
        } else if (
          // CS-2070 - Editable Upfront Discount / Late Fee must hold a valid percentage before saving.
          productRows.find(
            (row) =>
              (row.upfrontPricingEnabled && !isValidPercent(row.upfrontDiscount)) ||
              (row.lateFeeEnabled && !isValidPercent(row.lateFeePercent))
          )
        ) {
          let errorObj: SingleAlertInfo = {
            message: "Please enter a valid percentage (0-100) for Upfront Discount and Late Fee for each added product.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errorObj));
        } else {
          setShowOptionsPopup({
            message: "Do you really want to create the Product Agreement? ",
            show: true,
            variant: "confirm",
            buttons: [
              {
                name: "Confirm",
                color: "primary",
                onClick: () => createAgreement(agreementObjWithUpfrontPrice),
              },
              { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
            ],
          });
        };
      };
  };

  const extractIndex = (str: string) => {
    const match = str.match(/\[(\d+)\]/);
    return match ? Number(match[1]) : null;
  };

  const createAgreement = async (agreementObj: any) => {
    cancelOptionPopup();
    try {
      const response = await dispatch(createProductAgreement( { agreementData : agreementObj, interceptorOption: { skipErrorPopup: true } } ));
      if(response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {

        const dataArray = response?.payload?.data || [];

        const createdProviders = dataArray
            .flatMap((item: any) => item.agreementCreatedProviders || []);
        const notCreatedProviders = dataArray
            .flatMap((item: any) => item.agreementNotCreatedProviders || []);

        const seenCreatedProviderIds = new Set();
        const distinctCreatedProviders = createdProviders.filter((provider: any) => {
            if (!seenCreatedProviderIds.has(provider.providerId)) {
                seenCreatedProviderIds.add(provider.providerId);
                return true;
            }
            return false;
        });

        const seenNotCreatedProviderIds = new Set();
        const distinctNotCreatedProviders = notCreatedProviders.filter((provider: any) => {
            if (!seenNotCreatedProviderIds.has(provider.providerId)) {
                seenNotCreatedProviderIds.add(provider.providerId);
                return true;
            }
            return false;
        });

        const createdNames = distinctCreatedProviders
            .map((provider: any) => `${provider.title} ${provider.firstName} ${provider.lastName}`)
            .join(", ");
        const notCreatedNames = distinctNotCreatedProviders
            .map((provider: any) => `${provider.title} ${provider.firstName} ${provider.lastName}`)
            .join(", ");

        let alertMessage = "";

        if (distinctCreatedProviders.length > 0) {
          alertMessage += `\nFor: ${createdNames}.`;
        }

        let successObj: SingleAlertInfo = {
          title: "Product Agreement(s) have been created.",
          message: alertMessage,
          alertType: "success",
        };
        dispatch(setSingleAlertObj(successObj));
        navigate("/practice_product_agreement");
      } else if( response?.payload?.code === RESPONSE_CODES.FAILED_CODE) {
        if(response?.payload?.message === MSG_ACTIVE_PRODUCT_AGREEMENT_FOUND && response?.payload?.errors) {
          let productNames: string[] = [];
          response?.payload?.errors.map((error: any) => {
            const index = extractIndex(error.field);
            if(index !== null) {
              productNames.push(productRows[index].name)
              let errObj: SingleAlertInfo = {
                message: `Agreement(s) already available for ( ${productNames} )`,
                alertType: "error",
              };
              dispatch(setSingleAlertObj(errObj));
            }
          })
        } else {
          let errObj: SingleAlertInfo = {
            message: response?.payload?.message || "Something went wrong.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errObj));
        }
      }
    } catch (error) {
      let errObj: SingleAlertInfo = {
        message: "Something went wrong while creating the agreement.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errObj));
    }
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleDateChangeEffective = (date: Date | null) => {
    setValue("effectiveDate", dayjs(date).format("MM/DD/YYYY"));
    let products: PracticeProductInfo[] = getValues("practiceProducts");
    if (products.length > 0) {
      setIsEfctDateDisable(true);
    }
    setValue("product", "");
  };

  // const handleDateChangeInactive = (date: Date | null) => {
  //   setValue("inactiveDate", dayjs(date).format("MM/DD/YYYY"));
  // };

  const handlePracticeDetailsShow = () => {
    const selectedPracticeId = getValues("practiceId");

    if (selectedPracticeId) {
      setShowPracticeDetails(true);
    } else {
      errorObj.message = "Please select a Practice,";
      dispatch(setSingleAlertObj(errorObj));
    }
  };

  const MACOptions: Option[] = useMemo(() => {
    const options: Option[] = [{ label: "Select", value: "" }];
    macInfoList?.map((mac) => {
      options.push({
        value: mac.macId,
        label: mac.macName
      })
    });
    return options
  }, [macInfoList]);

  const selectedManufacturerId = useWatch({
    control,
    name: "manufacturerId"
  });
  const selectedManufacturer = manufacturerInfoList?.find(
    (manufacturer) => Number(manufacturer.manufacturerId) === Number(selectedManufacturerId)
  );
  const canShowGrouping =
    !!selectedManufacturerId && isGroupingEnabledManufacturer(selectedManufacturer);
  const watchedGroupingProductIds = useWatch({
    control,
    name: "groupingProductIds"
  });

  const manufacturerOptions: Option[] = useMemo(() => {
    const options: Option[] = [{ label: "Select", value: "" }];
    const productManufacturers = new Map<number, string>();
    (productsByUserRole || []).forEach((product) => {
      if (product.productStatus === "ACTIVE" && product.manufacturerId) {
        productManufacturers.set(Number(product.manufacturerId), product.manufacturerName || "");
      }
    });
    const sortedManufacturers = sortNames(manufacturerInfoList || [], "manufacturerName");
    if (sortedManufacturers.length > 0) {
      sortedManufacturers.forEach((manufacturer: any) => {
        if (manufacturer.inactiveDate) {
          return;
        }
        if (productManufacturers.size > 0 && !productManufacturers.has(Number(manufacturer.manufacturerId))) {
          return;
        }
        options.push({
          value: manufacturer.manufacturerId,
          label: manufacturer.manufacturerName,
        });
      });
    } else {
      Array.from(productManufacturers.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .forEach(([id, name]) => {
          options.push({ value: id, label: name || String(id) });
        });
    }
    return options;
  }, [manufacturerInfoList, productsByUserRole]);

  // CS-1747 START - Remove interested-product-category restriction in Create Agreement flow
  const productOptions: Option[] = useMemo(() => {
    const options: Option[] = [];
    const activeProducts = productsByUserRole.filter(
      (product) => product.productStatus === "ACTIVE"
        && (!selectedManufacturerId
          || Number(product.manufacturerId) === Number(selectedManufacturerId))
    );

    const sortedProductList = sortNames(activeProducts, "productName");
    sortedProductList.forEach((prod: any) => {
      options.push({ value: prod.productId, label: prod.productName });
    });

    return options;
  }, [productsByUserRole, selectedManufacturerId]);
  // CS-1747 END
  const practiceOptions: Option[] = useMemo(() => {
    return sortNames((practiceLookupOptions || []) as any, "label");
  }, [practiceLookupOptions]);

  const effectiveDate = useWatch({
    control,
    name: 'effectiveDate'
  });

  useEffect(() => {
    if(dayjs(effectiveDate).isValid()) {
      setMaxInactiveDate(dayjs(effectiveDate).add(20, 'year'));
      setMinInactiveDate(dayjs(effectiveDate));
    }
  }, [effectiveDate])


  const fetchPracticeInfoAndPracticeProducts = async (practiceIdParam?: string | number | null) => {
    const selectedPracticeIdRaw = practiceIdParam ?? getValues("practiceId");
    const selectedPracticeId = Number(selectedPracticeIdRaw);
    if (!Number.isFinite(selectedPracticeId)) {
      return;
    }
    if (inFlightPracticeFetchIdRef.current === selectedPracticeId) {
      return;
    }
    if (lastFetchedPracticeIdRef.current === selectedPracticeId) {
      return;
    }

    inFlightPracticeFetchIdRef.current = selectedPracticeId;
    try {
      const [responsePractice, responseProduct] = await Promise.all([
        dispatch(fetchPracticeByIdData(String(selectedPracticeId))),
        dispatch(fetchPracticeProductsData(String(selectedPracticeId))),
      ]);
      setPracticeData(responsePractice.payload.data);
      setPracticeProductData(responseProduct.payload.data);
      lastFetchedPracticeIdRef.current = selectedPracticeId;

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      inFlightPracticeFetchIdRef.current = null;
    }
  };

  const handleDeleteMainRow = (productId: number) => {
    const relatedRows = productRows.filter((row) => row.productId === productId);
    relatedRows.forEach((row) => {
      if (row.productPriceId) {
        handleClose(row.productPriceId); // Pass productPriceId to handleClose
      }
    });
    resetDiscountComponent(productId);
  };

  const resetDiscountComponent = (productId: number) => {
    //@ts-ignore
    setValue(`customDiscountMain${productId}`, "");
    //@ts-ignore
    setValue(`customPriceMain${productId}`, "");
    //@ts-ignore
    setValue(`radioMain${productId}`, "");
  };
  

  const closeComponentMainRow = (productId: number): JSX.Element => {
    return (
      <FormControl>
        <CustomIconButton
          color="warning"
          size="medium"
          variant="outlined"
          onClick={() => handleDeleteMainRow(productId)}
          sx={tableDeleteBtnSx}
        >
          <DeleteOutlineOutlined fontSize="small" />
        </CustomIconButton>
      </FormControl>
    );
  };

  const mainDiscountComponent = (
    productId: number,
    control: any,
    setValue: any,
    getValues: any
  ): JSX.Element => {
  
    const radioOptions = [
      {
        value: "customDiscountMain",
        label: (
          <Box sx={clickToSelectFieldWrapSx(140)} onClick={focusFieldAfterSelect}>
            <CustomTextField
              control={control}
              controllerName={`customDiscountMain${productId}`}
              label=""
              placeholder="Discount %"
              onChange={(e) => handleMainDiscountChange(e, "customDiscountMain", productId)}
              type="number"
              rules={{
                min: { value: 0, message: "Min 0%" }, //CS-1530
                max: { value: 100, message: "Max 100%" },
              }}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                const value = target.value;
                if (value.includes('.')) {
                  const [intPart, decPart] = value.split('.');
                  if (decPart.length > 2) {
                    target.value = `${intPart}.${decPart.slice(0, 2)}`;
                  }
                }
              }}
              size="small"
              disabled = {isCustomPriceRadio}
            />
          </Box>
        ),
      },
      {
        value: "customPriceMain",
        label: (
          <Box sx={clickToSelectFieldWrapSx(140)} onClick={focusFieldAfterSelect}>
            <CustomCurrencyField
              control={control}
              controllerName={`customPriceMain${productId}`}
              label=""
              placeholder="Custom Price"
              onChange={(e) => e && handleMainDiscountChange(e, "customPriceMain", productId)}
              size="small"
              disabled = {isCustomDiscountRadio}
            />
          </Box>
        ),
      },
    ];
  
    return (
      <Grid container spacing={1} justifyContent="center" alignItems="center">
        <Grid item>
          <CustomRadioButton
            control={control}
            controllerName={`radioMain${productId}`}
            options={radioOptions}
            onChange={(e) => handleRadioChange(e.target.value, productId)}
            radioSx={tableRadioSx}
            radioLabelSx={tableRadioLabelSx}
          />
        </Grid>
      </Grid>
    );
  };

    //CS-1337 START
    const secondaryMainDiscountComponent = (): JSX.Element => {
      return (
        <Grid container spacing={1} justifyContent='center' alignItems="center">
        <Grid item>
          <CustomRadioButton
            control={control}
            defaultValue = "customPriceMain"
            controllerName={`secondaryRadioMain`}
            options={[{ value: "customPriceMain", label: "Custom Price" }]}
            radioSx={tableRadioSx}
            radioLabelSx={tableRadioLabelSx}
          />
        </Grid>
        </Grid>
      );
    }
    //CS-1337 END

  const handleRadioChange = (selectedValue: string, productId: number) => {
    productRows.forEach((row) => {
      if (row.productId === productId) {
        releaseTierChoice(row.productPriceId);
        //@ts-ignore
        setValue(`radio.discount${row.productPriceId}`, selectedValue === "customDiscountMain" ? "customDiscount" : "customPrice");
  
        if (selectedValue === "customDiscountMain") {
          //@ts-ignore
          setValue(`customPrice.customPrice${row.productPriceId}`, "");
          //@ts-ignore
          setValue(`discount.discount${row.productPriceId}`, "");
        } else {
          //@ts-ignore
          setValue(`customPrice.customPrice${row.productPriceId}`, row.customPrice ? priceFormatter(row.customPrice) : "");
          //@ts-ignore
          setValue(`discount.discount${row.productPriceId}`, "");
        }
        let products: PracticeProductInfo[] = getValues("practiceProducts");
        setProductRows((prevRows) =>
          prevRows.map((r) =>
            r.productPriceId === row.productPriceId
              ? { ...r, 
                extendedPrice:  selectedValue === "customPriceMain" ? priceFormatter(row.customPrice) : null,
                discount: discountComponent(undefined, r.allowableUnitPrice, r.customPrice, selectedValue === "customDiscountMain" ? "customPrice" : "customDiscount", r.productPriceId )
              }
              : r
          )
        );
        products?.map((product) => {
          if (product.productPriceId === row?.productPriceId) {
            product.discountType = selectedValue === "customPriceMain" ? "CUSTOM" : "";
            product.extendedPrice = selectedValue === "customPriceMain" ? row?.customPrice : null;
          }
        }); 
        setValue("practiceProducts", products);
      }

    });
    if (selectedValue === "customDiscountMain") {
      //@ts-ignore
      setValue(`customPriceMain${productId}`, "");
      setCustomPriceRadio(false);
      setCustomDiscountRadio(true);
    } else if(selectedValue === "customPriceMain") {
      //@ts-ignore
      setValue(`customDiscountMain${productId}`, "");
      setCustomPriceRadio(true);
      setCustomDiscountRadio(false);
    }

    
  };
  

  const handleMainDiscountChange = (event: any, type: string, productId: number) => {
    productRows.forEach((row) => {
      let products: PracticeProductInfo[] = getValues("practiceProducts");
      if (row.productId === productId) {
        if(type === "customPriceMain" ){
          //@ts-ignore
            setValue(`radio.discount${row.productPriceId}`, "customPrice");
            //@ts-ignore
            setValue(`discount.discount${row?.productPriceId}`,"");
            //@ts-ignore
            setValue(`customPrice.customPrice${row?.productPriceId}`,event?.target?.value || "");
            products = setCustomPrice(row.allowableUnitPrice, convertToFloat(event?.target?.value), products, row.customPrice, row?.productPriceId)
        }else{
           //@ts-ignore
            setValue(`radio.discount${row.productPriceId}`, "customDiscount");
            //@ts-ignore
            setValue(`discount.discount${row?.productPriceId}`,event?.target?.value || "");
            //@ts-ignore
            setValue(`customPrice.customPrice${row?.productPriceId}`,"");
            products = setDiscount(row.allowableUnitPrice, event?.target?.value, products, row?.productPriceId)
        }
        setValue("practiceProducts", products);
      }
    });
  };

  const updateExtendedPrice = (row: any, type: string, value: string) => {
    
    let newExtendedPrice :any = null;
  
    if (type === "customDiscountMain") {
      const percentageValue = value  ;
      const allowableUnitPriceValue = row.allowableUnitPrice;

      newExtendedPrice = calculateValue(percentageValue, allowableUnitPriceValue);

      
    } else if (type === "customPriceMain") {
      newExtendedPrice = convertToFloat(value);
    }
    // Update the extended price in productRows
    setProductRows((prevRows) =>
      prevRows.map((r) =>
        r.productPriceId === row.productPriceId
          ? { ...r, extendedPrice:  priceFormatter(newExtendedPrice) }
          : r
      )
    );
  };

  // CS-1845 - Show Upfront Price only when at least one selected product has upfront pricing enabled.
  const showUpfrontPricingColumn = useMemo(
    () => productRows.some((row) => row.upfrontPricingEnabled),
    [productRows]
  );

  // CS-1764 - Show Late Fee column only when at least one selected product has late fee enabled.
  const showLateFeeColumn = useMemo(
    () => productRows.some((row) => row.lateFeeEnabled),
    [productRows]
  );

  const visibleProductColumns = useMemo(
    () => {
      let columns = [...productColumns];
      // CS-1845 - Add Upfront Price column if enabled
      if (showUpfrontPricingColumn) {
        columns = [
          ...columns.slice(0, 5),
          { id: "upfrontPrice", label: "Upfront Discount % / Price", minWidth: 260 },
          ...columns.slice(5),
        ];
      }
      // CS-1764 - Add Late Fee column if enabled
      if (showLateFeeColumn) {
        columns = [
          ...columns.slice(0, -1),
          { id: "lateFee", label: "Late Fee %", minWidth: 170 },
          ...columns.slice(-1),
        ];
      }
      if (!canManageTierPrice) {
        columns = columns.filter((column) => column.id !== "tier");
      }
      return columns;
    },
    [showUpfrontPricingColumn, showLateFeeColumn, canManageTierPrice]
  );

  const visibleSubColumns = useMemo(
    () => {
      let columns = [...subColumns];
      // CS-1845 - Add Upfront Price column if enabled
      if (showUpfrontPricingColumn) {
        columns = [
          ...columns,
          { id: "upfrontPrice", label: "Upfront Price", minWidth: 110 },
        ];
      }
      // CS-1764 - Add Late Fee column if enabled
      if (showLateFeeColumn) {
        columns = [
          ...columns,
          { id: "lateFee", label: "Late Fee %", minWidth: 100 },
        ];
      }
      if (!canManageTierPrice) {
        columns = columns.filter((column) => column.id !== "tier");
      }
      return columns;
    },
    [showUpfrontPricingColumn, showLateFeeColumn, canManageTierPrice]
  );

  return (
    <>
      <PracticeDetailsPopup
        open={showPracticeDetails}
        onClose={() => setShowPracticeDetails(false)}
        practiceId={getValues("practiceId")}
      />
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => {
            // CS-2070 - Dismissing (ESC / backdrop / X) a pending override confirm must also revert the edit.
            if (pendingOverrideRevertRef.current) {
              pendingOverrideRevertRef.current();
              pendingOverrideRevertRef.current = null;
            }
            setShowOptionsPopup({ message: "", show: false });
          }}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
        <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
          <PageBackButton tooltip={null} />
          <Box data-testid="header">
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
              Agreements / Create
            </Typography>
            <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
              Product Agreement
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
              Create a new product agreement for a practice.
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<DescriptionOutlined fontSize="small" />} title="Agreement Details" />
              <Box sx={{ px: { xs: 2, md: 2.8 }, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Practice" required />
                    <Box sx={dropdownWrapSx(!!errors.practiceName)}>
                      <CustomDropdownField
                        label=""
                        name="practiceName"
                        options={practiceOptions}
                        control={control}
                        size="small"
                        onChangeSelect={handleChangePracticeName}
                        rules={{ required: "practice is Required." }}
                        disabled={isPracticeDisable}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md="auto">
                    <Button
                      type="button"
                      variant="outlined"
                      removeUpperCase
                      onClick={() => handlePracticeDetailsShow()}
                      startIcon={<Details />}
                      fullWidth
                      sx={{ ...outlineActionBtnSx, minWidth: { sm: 160 } }}
                    >
                      Practice Details
                    </Button>
                  </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Effective Date" required />
                    <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                      <CustomDatePicker
                        label=""
                        openTo="day"
                        controllerName="effectiveDate"
                        control={control}
                        size="small"
                        onChange={handleDateChangeEffective}
                        disabled={isEfctDateDisable}
                        rules={{
                          required: "Effective Date is required.",
                          validate: {
                            isValidDate: (
                              value:
                                | string
                                | number
                                | Date
                                | dayjs.Dayjs
                                | null
                                | undefined
                            ) =>
                              dayjs(value, "MM/DD/YYYY", true).isValid() ||
                              "Invalid date format.",
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="MAC" />
                    <Box sx={dropdownWrapSx()}>
                      <CustomDropdownField
                        control={control}
                        name="practiceMacDropDown"
                        options={MACOptions}
                        label=""
                        size="small"
                        disabled={isMacDisable}
                        onChangeSelect={handleChangePracticeMacDropDown}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Manufacturer" />
                    <Box sx={dropdownWrapSx()}>
                      <CustomDropdownField
                        control={control}
                        name="manufacturerId"
                        options={manufacturerOptions}
                        label=""
                        size="small"
                        onChangeSelect={handleChangeManufacturer}
                      />
                    </Box>
                  </Grid>
                  {canShowGrouping && (
                    <Grid item xs={12} md="auto" sx={{ display: "flex", alignItems: "center", pb: 0.5 }}>
                      <CustomCheckbox
                        id="checkbox-agreementGrouping"
                        control={control}
                        controllerName="groupingChecked"
                        label="Group multiple products in a single agreement"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setValue("agreementMode", checked ? "GROUPING" : "SINGLE");
                        }}
                      />
                    </Grid>
                  )}
                </Grid>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="Product" />
                    <Box sx={{
                      ...dropdownWrapSx(!!errors.product),
                      "& .MuiAutocomplete-root .MuiInputBase-input": {
                        padding: "2.5px 4px 2.5px 8px !important",
                      },
                      "& .MuiChip-root[title='Select All Products']": {
                        display: "none",
                      },
                    }}>
                      <CustomMultipleDropDown
                        control={control}
                        name="groupingProductIds"
                        options={productOptions.length > 1
                          ? [{ value: ALL_GROUPING_PRODUCTS_VALUE, label: "Select All Products" }, ...productOptions]
                          : productOptions}
                        sortOptions={false}
                        placeholderText="Select product"
                        size="small"
                        onChangeSelect={handleGroupingProductsChange}
                      />
                      {/* <CustomDropdownField
                        control={control}
                        name="product"
                        options={productOptions}
                        label=""
                        size="small"
                        onChangeSelect={handleProductSelect}
                      /> */}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md="auto">
                    <Button
                      type="button"
                      variant="outlined"
                      removeUpperCase
                      onClick={() => { void verifyAddProduct(); }}
                      disabled={isAddingGroupingProducts}
                      startIcon={<AddCircleOutlineOutlined />}
                      fullWidth
                      sx={{ ...outlineActionBtnSx, minWidth: { sm: 150 } }}
                    >
                      {isAddingGroupingProducts ? "Adding..." : "Add Product(s)"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader
                icon={<Inventory2Outlined fontSize="small" />}
                title={`Selected Products (${new Set(productRows.map((row) => String(row.productId))).size})`}
              />
              <Box sx={{ px: { xs: 1.5, md: 2.8 }, py: 2 }}>
                {/* <CustomTable
                  columns={productColumns}
                  rows={productRows}
                  disablePagination={true}
                  emptyText="No Product(s) Found"
                  actionComponent={(row: any) => closeComponent(row.productPriceId )}
                /> */}
                <Box sx={productTableWrapSx}>
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table sx={productTableSx}>
                      {/* Main Table Header */}
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell style={{ minWidth: 40, fontWeight: "bold" }}>#</TableCell>
                            {visibleProductColumns.map((column) => (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                style={{ minWidth: column.minWidth, fontWeight: "bold" }}
                              >
                                {column.label}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productRows
                          .reduce<GroupedRow[]>((acc, row) => {
                            const isProductWithSKU = !!row.sku && !!row.price;

                            // Group rows by product for sub-tables
                            const existingMainRow = acc.find(
                              (item) =>
                                item.mainRow.name === row.name &&
                                isProductWithSKU &&
                                item.mainRow.productId === row.productId
                            );

                            if (!existingMainRow && isProductWithSKU) {
                              acc.push({ mainRow: { ...row, extra: true }, subRows: [row] });
                            } else if (existingMainRow && isProductWithSKU) {
                              existingMainRow.subRows.push(row);
                            } else {
                              acc.push({ mainRow: row, subRows: [] });
                            }
                            return acc;
                          }, [])
                          .map(({ mainRow, subRows }: GroupedRow, mainIndex: number) => (
                            <React.Fragment key={mainRow?.productPriceId || mainIndex}>
                              {/* Main Row */}
                              <TableRow
                              sx={{
                                backgroundColor: colors.grey[900], // Light gray for extra rows, white for others
                                "&:hover": {
                                  backgroundColor: isDarkModal ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)",
                                },
                              }}>
                                <TableCell style={{ minWidth: 80 }}>
                                  {subRows.length > 0 && (
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleCollapseToggle(mainIndex)}
                                    >
                                      {collapsedState[mainIndex] ? (
                                        <KeyboardArrowDown />
                                       
                                      ) : (
                                        <KeyboardArrowUp />
                                      )}
                                    </IconButton>
                                  )}
                                </TableCell>
                                <TableCell style={{ minWidth: 40 }}>{mainIndex + 1}</TableCell>
                                <TableCell style={{ minWidth: 100  ,maxWidth: 130 }}>{mainRow.name}</TableCell>
                                {/* <TableCell>{mainRow.extra ? "-" : mainRow.sku || ""}</TableCell> */}
                                <TableCell style={{ minWidth: 100 }}>{mainRow.extra ? "-" : mainRow.price || "No Price"}</TableCell>
                                <TableCell align="center" style={{ minWidth: 100 }}>{mainRow.extra ?  (mainRow.customPrice !== null && mainRow.customPrice !== undefined )?secondaryMainDiscountComponent(): mainDiscountComponent(mainRow.productId, control, setValue, getValues) : mainRow.discount || "N/A"}</TableCell>
                                {canManageTierPrice && <TableCell style={{ minWidth: 150 }}>{mainRow.extra ? "-" : mainRow.tier || "-"}</TableCell>}
                                <TableCell style={{ minWidth: 100 }}>{mainRow.extra ? "-" : mainRow.extendedPrice || "N/A"}</TableCell>
                                {showUpfrontPricingColumn && (
                                  <TableCell style={{ minWidth: 260, verticalAlign: "middle" }}>
                                    {upfrontDiscountEditorComponent(mainRow)}
                                  </TableCell>
                                )}
                                {showLateFeeColumn && (
                                  <TableCell style={{ minWidth: 170, verticalAlign: "middle" }}>{lateFeeEditorComponent(mainRow)}</TableCell>
                                )}
                                <TableCell style={{ minWidth: 50 }}>{mainRow.extra ? closeComponentMainRow(mainRow.productId) : closeComponent(mainRow.productPriceId)}</TableCell>
                              </TableRow>


                              {/* Sub-Table */}
                              {!collapsedState[mainIndex] && mainRow.extra && (
                                <TableRow>
                                  <TableCell colSpan={visibleProductColumns.length + 2} style={{ padding: 0 }}>
                                    <Box sx={{ m: 2, ...productTableWrapSx }}>
                                      <TableContainer sx={{ overflowX: "auto" }}>
                                      <Table sx={productTableSx}>
                                        <TableHead>
                                          <TableRow>
                                          {visibleSubColumns.map((column) => (
                                            <TableCell
                                              key={column.id}
                                              align={column.align}
                                              style={{ minWidth: column.minWidth, fontWeight: "bold" }}
                                            >
                                              {column.label}
                                            </TableCell>
                                          ))}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {subRows.map((subRow, subRowIndex) => (
                                            <TableRow key={`${mainRow?.productId}-${mainRow?.productPriceId}`}>
                                              <TableCell style={{ minWidth: 100 }}>{subRow.sku}</TableCell>
                                              <TableCell style={{ minWidth: 100 }}>{subRow.price}</TableCell>
                                              <TableCell align="center" style={{ minWidth: 100 }}>{subRow.discount}</TableCell>
                                              {canManageTierPrice && <TableCell style={{ minWidth: 150 }}>{subRow.tier || "-"}</TableCell>}
                                              <TableCell style={{ minWidth: 100 }}>{subRow.extendedPrice}</TableCell>
                                              {showUpfrontPricingColumn && (
                                                <TableCell style={{ minWidth: 110 }}>{upfrontPriceComponent(subRow)}</TableCell>
                                              )}
                                              {/* CS-1764 - Late Fee column in sub-table */}
                                              {showLateFeeColumn && (
                                                <TableCell style={{ minWidth: 100 }}>{lateFeeComponent(subRow)}</TableCell>
                                              )}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>

                                      </TableContainer>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={1.2}
              justifyContent="flex-end"
              mt={0.5}
            >
              <Button
                type="submit"
                startIcon={<Save />}
                removeUpperCase
                fullWidth
                sx={{
                  ...primaryBtnSx,
                  minWidth: { sm: 180 },
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: mTokens.accent,
                  boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                  color: "#fff",
                  "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
                  "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                Create Agreement
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => navigate(-1)}
                startIcon={<Cancel />}
                removeUpperCase
                fullWidth
                sx={{
                  ...cancelBtnSx,
                  minWidth: { sm: 140 },
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
                  "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default CreateNewAgreementNew;
